import React, { useState, useCallback } from 'react';
import { Input } from './input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Search, X } from 'lucide-react';
import { Button } from './button';

const SearchFilterBar = ({ 
  searchPlaceholder = "Search...", 
  onSearch, 
  filters = [],
  filterValues = {},
  onFilterChange
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [debounceTimer, setDebounceTimer] = useState(null);

  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => {
      onSearch(value);
    }, 400);
    setDebounceTimer(timer);
  }, [debounceTimer, onSearch]);

  const handleClear = () => {
    setSearchValue('');
    onSearch('');
    filters.forEach(f => onFilterChange(f.key, ''));
  };

  const hasActiveFilters = searchValue || Object.values(filterValues).some(v => v && v !== '');

  return (
    <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="search-filter-bar">
      <div className="relative flex-1 min-w-[200px] max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
        <Input
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-9 h-9"
          data-testid="search-input"
        />
      </div>
      {filters.map((filter) => (
        <Select
          key={filter.key}
          value={filterValues[filter.key] || ''}
          onValueChange={(value) => onFilterChange(filter.key, value === '__all__' ? '' : value)}
        >
          <SelectTrigger className="w-[150px] h-9" data-testid={`filter-${filter.key}`}>
            <SelectValue placeholder={filter.label} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All {filter.label}</SelectItem>
            {filter.options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ))}
      {hasActiveFilters && (
        <Button variant="ghost" size="sm" onClick={handleClear} className="h-9 px-2 text-neutral-500" data-testid="clear-filters">
          <X className="h-4 w-4 mr-1" /> Clear
        </Button>
      )}
    </div>
  );
};

export { SearchFilterBar };
export default SearchFilterBar;
